import InactivityTimeoutModalController from '../../modules/invigilatorMenu/modalCtrl/InactivityTimeoutModalController';

/**
 * InactivityTimeoutService is a service that monitors user activity and triggers a modal
 * when the user is inactive for a specified period.
 */
class InactivityTimeoutService {
    /**
     * Constructor for InactivityTimeoutService.
     * @param {Object} $bridges - The bridges service.
     * @param {Object} $document - The document service.
     * @param {Object} $interval - The interval service.
     * @param {Object} $uibModal - The UI Bootstrap modal service.
     * @param {Object} TaskCountdownService - The task countdown service.
     */
    constructor($bridges, $document, $interval, $uibModal, TaskCountdownService) {
        this.$bridges = $bridges;
        this.$document = $document;
        this.$interval = $interval;
        this.$uibModal = $uibModal;
        this.TaskCountdownService = TaskCountdownService;

        this._logIdentifier = 'InactivityTimeoutService';
        this.$logger = $bridges.logger();
        this._taskCountdown = TaskCountdownService.getInstance();

        this._eventList = 'mousemove keydown DOMMouseScroll mousewheel mousedown touchstart';
        this._idleTime = window.IB.timers.inactivityTimeoutIdleTime;
        this._isIdle = false;
        this._date = null;
        this._time = null;
        this._idleEndTime = null;
        this._checkIdleInterval = null;
        this._isStarted = false;
        this._lastTaskTime = null;
        this._stopEventTrigger = false;
        this._debugMode = false;
        this.MOUSE_TOLERANCE = 10;
        this._modalInstance = null;
    }

    /**
     * Resets the idle timer.
     * @param {string} reason - The reason for resetting the idle timer.
     * @returns {boolean} - Returns false if the event trigger is stopped.
     */
    resetIdle(reason) {
        if (this._stopEventTrigger) {
            if (this._debugMode) {
                console.log('ITS skipping resetIdle-' + reason);
            }
            return false;
        }

        if (this._debugMode) {
            console.error('ITS resetIdle-' + reason + " *****************************************************");
        }

        this._date = new Date();
        this._time = this._date.getTime();  // in ms
        this._idleEndTime = this._time + this._idleTime; // reset end time

        this._lastTaskTime = this._taskCountdown.getElapsedMillis();
    }

    /**
     * Checks if the user is idle and triggers the modal if necessary.
     */
    checkIdle() {
        if (this._taskCountdown.getMode() !== 'task') {
            this.stopWatch();
        }
        if (!this._taskCountdown.isRunning || this._taskCountdown.isPaused()) {
            return false;
        }

        this._date = new Date();
        this._time = this._date.getTime();  // in ms

        if (this._time > this._idleEndTime) {
            this._isIdle = true;
            this.stopWatch();

            this._taskCountdown.recordTelemetry('inactivity.idle_start', 'warning', {
                idleTimeMs: this._idleTime,
                visibilityState: document.visibilityState || 'unknown',
                elapsedMs: this._taskCountdown.getElapsedMillis(),
            });

            this._modalInstance = this.$uibModal.open({
                templateUrl: "/views/ib/modules/invigilatorMenu/modal/inactivityTimeoutModal.html",
                windowClass: "inactivity-pause-modal",
                transclude: false,
                backdrop: 'static',
                keyboard: false,
                size: 'lg',
                controller: InactivityTimeoutModalController,
                resolve: {
                    lastTaskTime: () => this._lastTaskTime
                }
            });

            this._modalInstance.result.then(() => {
                this._taskCountdown.recordTelemetry('inactivity.idle_end', 'info', {
                    reason: 'resolved',
                    visibilityState: document.visibilityState || 'unknown',
                });
                this._isIdle = false;
                this._modalInstance = null;
            }, (dismissReason) => {
                // Skip telemetry when dismissed programmatically by clock expiry —
                // dismissIdle() already emits inactivity.dismissed_by_expiry
                if (dismissReason !== 'countdownExpired') {
                    this._taskCountdown.recordTelemetry('inactivity.idle_end', 'info', {
                        reason: 'dismissed',
                        visibilityState: document.visibilityState || 'unknown',
                    });
                }
                this._isIdle = false;
                this._modalInstance = null;
            });
        }
    }

    /**
     * Starts monitoring user activity.
     * @returns {boolean} - Returns false if the watch is already started or if the countdown mode is not valid.
     */
    startWatch() {
        if (this._debugMode) {
            console.log("ITS startWatch");
        }
        if (this._isStarted || (this._taskCountdown.getMode() !== 'task' && this._taskCountdown.getMode() !== 'readingTime')) {
            if (this._debugMode) {
                console.log("ITS startWatch: not starting " + this._isStarted + "-countdown-mode:" + this._taskCountdown.getMode());
            }
            return false;
        }

        this.resetIdle('startWatch');
        this._isIdle = false;
        this._checkIdleInterval = this.$interval(this.checkIdle.bind(this), 10000);

        let lastMoveX = 0, lastMoveY = 0;

        const onWatchEvent = (e) => {
            let details = '',
                isInsignificantEvent = false,
                deltaX = -1,
                deltaY = -1;

            // Mousemove: Calculate how far the mouse has been moved
            if (e.type == 'mousemove') {
                // We just take absolute deltas, the direction doesn't matter at all
                deltaX = Math.abs(e.clientX - lastMoveX);
                deltaY = Math.abs(e.clientY - lastMoveY);
                // Memorize last x/y so we can continue calculating deltas
                lastMoveX = e.clientX;
                lastMoveY = e.clientY;
            }

            // Mousescroll: Just take the delta from the event
            if (e.type == 'mousewheel') {
                deltaX = Math.abs(e.originalEvent.wheelDeltaX);
                deltaY = Math.abs(e.originalEvent.wheelDeltaY);
            }

            // For either mousemove or mousewheel, check whether it was a
            // "insignificant" movement
            if (e.type == 'mousewheel' || e.type == 'mousemove') {
                // Compare added X and Y delta against a tolerance
                isInsignificantEvent = deltaX + deltaY < this.MOUSE_TOLERANCE;

                if (this._debugMode) {
                    details = '-x:' + e.clientX + '/' + deltaX + '-y:' + e.clientY + '/' + deltaY + '-->' + (isInsignificantEvent ? 'insignificant' : 'significant');
                }
            }

            if (this._debugMode) {
                console.log('ITS event-type:' + e.type + details);
            }

            // Only reset idle timer if it's a significant event
            if (!isInsignificantEvent) {
                this.resetIdle(e.type);
            }
        };

        this.$document.find('body').on(this._eventList, onWatchEvent);

        this._isStarted = true;
    }

    /**
     * Stops monitoring user activity.
     */
    stopWatch() {
        if (this._debugMode) {
            console.log("ITS stopWatch");
        }

        this.$interval.cancel(this._checkIdleInterval);
        this.$document.find('body').off(this._eventList);

        this._isStarted = false;
    }

    /**
     * Checks if the user is currently idle.
     * @returns {boolean} - True if the user is idle, false otherwise.
     */
    isIdle() {
        return this._isIdle;
    }

    /**
     * Programmatically dismisses the inactivity modal and clears idle state.
     * Used when the countdown expires while the inactivity modal is open,
     * so that the expiry navigation is not blocked.
     */
    dismissIdle() {
        this._taskCountdown.recordTelemetry('inactivity.dismissed_by_expiry', 'warning', {
            hadModal: !!this._modalInstance,
            visibilityState: document.visibilityState || 'unknown',
            elapsedMs: this._taskCountdown.getElapsedMillis(),
        });
        if (this._modalInstance) {
            this._modalInstance.dismiss('countdownExpired');
            this._modalInstance = null;
        }
        this._isIdle = false;
        this.stopWatch();
    }

    /**
     * Stops the event trigger for resetting the idle timer.
     */
    stopEventTrigger() {
        if (this._debugMode) {
            console.log("ITS stopEventTrigger");
        }
        this._stopEventTrigger = true;
    }

    /**
     * Starts the event trigger for resetting the idle timer.
     */
    startEventTrigger() {
        if (this._debugMode) {
            console.log("ITS startEventTrigger");
        }
        this._stopEventTrigger = false;
    }
}

export default InactivityTimeoutService;